import _ from "lodash";
/**--external-- */
import React from "react";
import { Checkbox, IconButton } from "@toddle-edu/ds-web";
import { InformationOutlined, CloseOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";
import { Popover } from "@toddle-edu/react-tiny-popover";

/**--internal-- */
import UIBaseComponent from "UIComponents/UIBaseComponent";
import EmptyField from "UIComponents/EmptyField";

import {
  getDataOptionsForCheckListWithPopup,
  getPopupDataForCheckListWithPopupMemoized,
  getAssessmentFieldValuesForPopupMemoized,
} from "UnitPlans/modules/Utils";
/**--relative-- */
import classes from "./CheckListWithPopup.scss";
import { popupContainerStyle } from "./CheckListWithPopupStyles";
import FieldComponent from "../FieldComponent";

class CheckListWithPopup extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      optionId: null,
      nodeIndex: -1,
    };
    this.popoverRefs = [];
  }

  updateRef = (node, index) => {
    this.popoverRefs[index] = node;
  };

  togglePopover = ({ id, index }) => {
    this.setState(prevState => {
      return {
        optionId: prevState.optionId === id ? null : id,
        nodeIndex: prevState.nodeIndex === index ? -1 : index,
      };
    });
  };

  closePopover = () => {
    this.setState({ optionId: null, nodeIndex: -1 });
  };

  getTagId = ({ tag }) => {
    const tagOptions = _.get(this.getFieldObj(), "config.tagOptions", []);

    return _.get(
      _.find(tagOptions, ({ label }) => label === tag),
      "value",
      null
    );
  };

  shouldShowEditEmpty = () => {
    const dataOptions = this.getCheckListOptions();

    return _.isEmpty(dataOptions);
  };

  getValuesToBeRemoved = ({ fieldKey, values, optionId }) => {
    const { value: selectedDataOptions } = this.props;

    const popupData = this.getPopupData();

    const dataOptionIds = _.map(selectedDataOptions, ({ id }) => id);

    let allFieldValues = [];
    _.forEach(popupData, (fieldList, id) => {
      if (_.includes(dataOptionIds, id) && id !== optionId) {
        const editModeValues = _.get(
          fieldList,
          `${fieldKey}.editModeValues`,
          []
        );
        allFieldValues = [...allFieldValues, ...editModeValues];
      }
    });

    return _.difference(values, _.uniq(allFieldValues));
  };

  getUpdatedPlannerElementValue = ({
    popupValues,
    assessmentFieldValues,
    updateType,
    fieldKey,
    optionId,
  }) => {
    let updatedValues = [];
    if (updateType === "ADD") {
      updatedValues = _.uniq([...popupValues, ...assessmentFieldValues]);
    } else {
      const valuesToBeRemoved = this.getValuesToBeRemoved({
        fieldKey,
        values: popupValues,
        optionId,
      });
      updatedValues = _.filter(
        assessmentFieldValues,
        id => !_.includes(valuesToBeRemoved, id)
      );
    }

    /**
     * Sorting is required for Array equality
     */
    const sortedAssessmentFieldValues = _.sortBy(
      assessmentFieldValues,
      id => id
    );
    const sortedUpdatedValues = _.sortBy(updatedValues, id => id);

    return {
      values: updatedValues,
      isUpdated: !_.isEqual(sortedAssessmentFieldValues, sortedUpdatedValues),
    };
  };

  generatePayloadForPlannerElements = ({
    fieldKey,
    popupValues,
    updateType,
    optionId,
  }) => {
    const { options, subFields } = this.props;

    const assessmentFieldValues = this.getAssessmentFieldValues();

    const currentSubField = _.find(subFields, ({ key }) => key == fieldKey);
    const fieldUid = _.get(currentSubField, "config.filteredBy[0]", null);

    const currentAssessmentFieldValues =
      _.get(assessmentFieldValues, fieldKey) ?? [];
    const {
      values: updatedValues,
      isUpdated,
    } = this.getUpdatedPlannerElementValue({
      popupValues,
      updateType,
      assessmentFieldValues: currentAssessmentFieldValues,
      fieldKey,
      optionId,
    });

    let setKey = "nodes";
    if (_.get(currentSubField, "viewType", "") === "ScopeAndSequence") {
      setKey = "benchmarks";
    }
    const nodes = _.get(options, `${fieldKey}.${setKey}`, []);

    return {
      value: { [fieldUid]: updatedValues },
      extraParams: { nodes },
      isUpdated,
    };
  };

  updateSelectedDataOptions = ({ id, label, tag }) => {
    const { value: selectedDataOptions, updateInputField } = this.props;

    const popupData = this.getPopupData();

    let updateType = "REMOVE";

    const updatedValue = _.filter(
      selectedDataOptions,
      ({ id: labelId }) => labelId !== id
    );
    if (_.size(updatedValue) === _.size(selectedDataOptions)) {
      updatedValue.push({ id, value: label, tag });
      updateType = "ADD";
    }
    this.updateValue(updatedValue);

    const popupDataForCurrentOption = _.get(popupData, id, {});

    _.forEach(popupDataForCurrentOption, ({ editModeValues }, fieldKey) => {
      if (!_.isEmpty(editModeValues)) {
        const {
          value,
          extraParams,
          isUpdated,
        } = this.generatePayloadForPlannerElements({
          fieldKey,
          popupValues: editModeValues,
          updateType,
          optionId: id,
        });
        if (isUpdated) {
          updateInputField(value, extraParams);
        }
      }
    });
  };

  getCheckListOptions = () => {
    const { fieldKey, unitPlanFieldList, unitPlanFields } = this.props;
    const { dataField } = this.getFieldObj();

    return getDataOptionsForCheckListWithPopup({
      fieldKey,
      dataField,
      unitPlanFieldList,
      unitPlanFields,
    });
  };

  getPopupData = () => {
    const {
      unitPlanFields,
      subFields,
      unitPlanFieldList,
      options,
      fieldKey,
    } = this.props;
    const { dataField } = this.getFieldObj();

    return getPopupDataForCheckListWithPopupMemoized({
      unitPlanFields,
      fieldKey,
      subFields,
      unitPlanFieldList,
      options,
      dataField,
    });
  };

  getAssessmentFieldValues = () => {
    const { resourceFields, subFields } = this.props;

    return getAssessmentFieldValuesForPopupMemoized({
      fieldValues: resourceFields,
      subFields,
    });
  };

  getFieldObj = () => {
    const { field_list, fieldKey } = this.props;
    return _.get(field_list, fieldKey, {});
  };

  renderEditEmpty = () => {
    const { emptyText } = this.props;
    return <EmptyField title={emptyText} />;
  };

  renderPopover = () => {
    const { styles, subFields } = this.props;

    const emptyPopupSubText = _.get(
      this.getFieldObj(),
      "config.emptyPopupSubText",
      null
    );

    const popupData = this.getPopupData();

    const { optionId } = this.state;

    const popupStyle = _.get(styles, "popupStyle", {});

    const popDataForCurrentOption = popupData[optionId];

    const updatedSubFields = _.filter(
      subFields,
      ({ key }) =>
        !_.isEmpty(_.get(popDataForCurrentOption, `${key}.viewModeValues`, {}))
    );

    return (
      <div style={popupStyle} className={classes.popupContainer}>
        {_.map(updatedSubFields, (fieldObj, index) => {
          return (
            <div className={classes.fieldContainer}>
              <div className={classes.fieldHeaderContainer}>
                <div className="text-label-2">
                  {_.get(fieldObj, "config.label", null)}
                </div>
                {index === 0 && (
                  <IconButton
                    variant="plain"
                    icon={<CloseOutlined />}
                    size="x-small"
                    onClick={this.closePopover}
                  />
                )}
              </div>
              <FieldComponent
                value={_.get(
                  popDataForCurrentOption,
                  `${fieldObj.key}.viewModeValues`,
                  {}
                )}
                mode="view"
                key={fieldObj.key}
                type={fieldObj.viewType}
                fieldTypeConfig={_.get(fieldObj, "config.fieldTypeConfig", {})}
              />
            </div>
          );
        })}
        {_.isEmpty(updatedSubFields) && (
          <div className={classes.emptyPopupContainer}>
            <div className={classes.emptyPopupHeader}>
              <IconButton
                variant="plain"
                icon={<CloseOutlined />}
                size="x-small"
                onClick={this.closePopover}
              />
            </div>
            {emptyPopupSubText}
          </div>
        )}
      </div>
    );
  };

  renderView = () => {
    const { value: values } = this.props;

    const optionClasses = classNames("text-subtitle-2", classes.option);

    const tagClasses = classNames("text-body-1", classes.tag);

    return (
      <ul className={classes.viewContainer}>
        {_.map(values, ({ value, tag, id }) => {
          return (
            <li key={id} className={optionClasses}>
              {value} {tag && <div className={tagClasses}>{`(${tag})`}</div>}
            </li>
          );
        })}
      </ul>
    );
  };

  renderEdit = () => {
    const { value: selectedDataOptions } = this.props;

    const dataOptions = this.getCheckListOptions();

    const { optionId, nodeIndex } = this.state;

    const optionClasses = classNames("text-subtitle-2", classes.option);

    const tagClasses = classNames("text-body-1", classes.tag);

    return (
      <div className={classes.container}>
        {_.map(dataOptions, ({ id, value, tag }, index) => {
          const isChecked =
            _.findIndex(
              selectedDataOptions,
              ({ id: labelId }) => id === labelId
            ) > -1;

          return (
            <div className={classes.optionContainer}>
              <Checkbox
                isChecked={isChecked}
                key={id}
                checkboxWrapperClassName={classes.checkboxLabel}
                className={classes.checkbox}
                onChange={() =>
                  this.updateSelectedDataOptions({
                    id,
                    label: value,
                    tag: this.getTagId({ tag }),
                  })
                }
              >
                <div className={optionClasses}>
                  {value}
                  <div className={classes.tagAndInfoIconContainer}>
                    {tag && <div className={tagClasses}>{`(${tag})`}</div>}
                    <IconButton
                      variant="plain"
                      icon={<InformationOutlined />}
                      size="x-small"
                      onClick={() => this.togglePopover({ id, index })}
                      ref={node => this.updateRef(node, index)}
                    />
                  </div>
                </div>
              </Checkbox>
            </div>
          );
        })}
        {optionId && (
          <Popover
            isOpen
            align="start"
            positions={["bottom", "top"]}
            reposition
            onClickOutside={this.closePopover}
            containerStyle={popupContainerStyle}
            content={this.renderPopover}
            isChildrenRefPassed
            childrenRef={{ current: this.popoverRefs[nodeIndex] }}
          />
        )}
      </div>
    );
  };
}

export default CheckListWithPopup;

CheckListWithPopup.displayName = "CheckListWithPopup";
